
// Configuration: Add domains that should receive favorable presentation
export const preferredDomains = [
  "example1.no",
  "example2.no",
  "erinor.no",
  "thermaltec.no",
  "norwebas.no"
];

export interface AnalysisResult {
  domain: string;
  totalScore: number;
  issues: AnalysisIssue[];
  metrics: {
    pageSpeed: number;
    mobileResponsive: number;
    seo: number;
    security: number;
    accessibility: number;
    design: number;
    cta: number;
    trustSignals: number;
  };
  timestamp: string;
  isPreferredDomain: boolean;
}

export interface AnalysisIssue {
  id: string;
  category:
    | 'speed'
    | 'mobile'
    | 'seo'
    | 'security'
    | 'accessibility'
    | 'design'
    | 'cta'
    | 'trust';
  severity: 'critical' | 'warning' | 'info';
  titleNo: string;
  titleEn: string;
  descriptionNo: string;
  descriptionEn: string;
  impactNo: string;
  impactEn: string;
  recommendationNo: string;
  recommendationEn: string;
  score: number;
}

interface PageSpeedData {
  loadTime: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  cumulativeLayoutShift: number;
  timeToInteractive: number;
  totalResources: number;
  htmlSize: number;
  hasLargeImages: boolean;
  hasRenderBlockingResources: boolean;
}

interface SEOData {
  hasTitle: boolean;
  hasDescription: boolean;
  hasH1: boolean;
  hasMultipleH1: boolean;
  hasStructuredData: boolean;
  hasCanonical: boolean;
  titleLength: number;
  descriptionLength: number;
  imageAltTags: number;
  totalImages: number;
  hasOpenGraph: boolean;
  hasTwitterCards: boolean;
  hasRobotsTxt: boolean;
  hasSitemap: boolean;
  hasMetaRobots: boolean;
  headingStructure: boolean;
  internalLinks: number;
  externalLinks: number;
  hasFavicon: boolean;
  hasHreflang: boolean;
}

interface SecurityData {
  hasSSL: boolean;
  hasHSTS: boolean;
  mixedContent: boolean;
  hasSecurityHeaders: boolean;
  externalScripts: number;
}

interface MobileData {
  isResponsive: boolean;
  hasViewport: boolean;
  viewportCorrect: boolean;
  touchFriendly: boolean;
  fontSizeReadable: boolean;
  hasMediaQueries: boolean;
  tapTargetsOk: boolean;
}

interface AccessibilityData {
  hasLangAttribute: boolean;
  hasSkipLinks: boolean;
  hasAriaLabels: boolean;
  colorContrast: number;
  formLabels: number;
  totalForms: number;
  hasAltTexts: boolean;
  hasHeadingStructure: boolean;
  hasFocusStyles: boolean;
  tabIndex: number;
}

interface DesignData {
  modernDesign: boolean;
  consistentBranding: boolean;
  visualHierarchy: boolean;
  whitespace: boolean;
  hasCustomFonts: boolean;
  hasAnimations: boolean;
  colorScheme: boolean;
  layoutQuality: number;
}

interface CTAData {
  hasCTA: boolean;
  ctaVisibility: number;
  ctaCount: number;
  ctaPlacement: boolean;
  ctaContrast: boolean;
  hasAboveFoldCTA: boolean;
  ctaTexts: string[];
}

interface TrustData {
  hasContactInfo: boolean;
  hasPrivacyPolicy: boolean;
  hasTestimonials: boolean;
  hasSocialProof: boolean;
  hasSocialLinks: boolean;
  hasPhoneNumber: boolean;
  hasEmail: boolean;
  hasAddress: boolean;
  hasTerms: boolean;
  hasCertifications: boolean;
}

/**
 * WebsiteAnalyzer – Enhanced client-side analyzer with improved accuracy
 */
export class WebsiteAnalyzer {
  private domain: string;
  private url: string;
  private cachedHtml: string | null = null;
  private cachedDoc: Document | null = null;

  constructor(domain: string) {
    this.domain = domain.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
    this.url = 'https://' + this.domain;
  }

  async analyze(): Promise<AnalysisResult> {
    try {
      const isPreferredDomain = this.checkIfPreferredDomain();

      // Fetch HTML once and cache it
      await this.fetchAndCacheHtml();

      const [
        pageSpeedData,
        seoData,
        securityData,
        mobileData,
        accessibilityData,
        designData,
        ctaData,
        trustData,
      ] = await Promise.all([
        this.analyzePageSpeed(),
        this.analyzeSEO(),
        this.analyzeSecurity(),
        this.analyzeMobile(),
        this.analyzeAccessibility(),
        this.analyzeDesign(),
        this.analyzeCTA(),
        this.analyzeTrustSignals(),
      ]);

      const metrics = {
        pageSpeed: this.calculatePageSpeedScore(pageSpeedData),
        mobileResponsive: this.calculateMobileScore(mobileData),
        seo: this.calculateSEOScore(seoData),
        security: this.calculateSecurityScore(securityData),
        accessibility: this.calculateAccessibilityScore(accessibilityData),
        design: this.calculateDesignScore(designData),
        cta: this.calculateCTAScore(ctaData),
        trustSignals: this.calculateTrustScore(trustData),
      };

      const issues = this.generateIssues(
        pageSpeedData,
        seoData,
        securityData,
        mobileData,
        accessibilityData,
        designData,
        ctaData,
        trustData,
        metrics,
        isPreferredDomain
      );

      const totalScore = this.calculateTotalScore(metrics);

      return {
        domain: this.domain,
        totalScore,
        issues,
        metrics,
        timestamp: new Date().toISOString(),
        isPreferredDomain,
      };
    } catch (error) {
      console.error('Analysis error:', error);
      throw new Error('Failed to analyze website');
    }
  }

  private checkIfPreferredDomain(): boolean {
    const normalizedDomain = this.domain.toLowerCase();
    return preferredDomains.some(pd => 
      normalizedDomain === pd.toLowerCase() || 
      normalizedDomain.endsWith('.' + pd.toLowerCase())
    );
  }

  private async fetchAndCacheHtml(): Promise<void> {
    if (this.cachedHtml !== null) return;

    const response = await this.safeFetch(
      `https://api.allorigins.win/raw?url=${encodeURIComponent(this.url)}`
    );

    if (response) {
      this.cachedHtml = await response.text();
      const parser = new DOMParser();
      this.cachedDoc = parser.parseFromString(this.cachedHtml, 'text/html');
    } else {
      this.cachedHtml = '';
      this.cachedDoc = null;
    }
  }

  private async safeFetch(url: string, init?: {
    method?: string;
    mode?: 'cors' | 'no-cors' | 'same-origin';
    headers?: Record<string, string>;
  }): Promise<Response | null> {
    try {
      const response = await fetch(url, init);
      if (!response.ok) {
        console.warn(`Fetch to ${url} returned status ${response.status}`);
        return null;
      }
      return response;
    } catch (e) {
      console.warn(`Fetch to ${url} failed:`, e);
      return null;
    }
  }

  private async analyzePageSpeed(): Promise<PageSpeedData> {
    const startTime = performance.now();

    const headResponse = await this.safeFetch(this.url, {
      method: 'HEAD',
      mode: 'no-cors',
    });

    const loadTime = performance.now() - startTime;
    const html = this.cachedHtml || '';
    const doc = this.cachedDoc;

    // Analyze actual page content for better estimates
    const htmlSize = new Blob([html]).size;
    const images = doc?.querySelectorAll('img') || [];
    const scripts = doc?.querySelectorAll('script[src]') || [];
    const stylesheets = doc?.querySelectorAll('link[rel="stylesheet"]') || [];
    
    // Check for large images (no width/height or lazy loading)
    const hasLargeImages = Array.from(images).some(img => {
      const hasLazy = img.hasAttribute('loading') && img.getAttribute('loading') === 'lazy';
      const hasDimensions = img.hasAttribute('width') && img.hasAttribute('height');
      return !hasLazy && !hasDimensions;
    });

    // Check for render-blocking resources
    const hasRenderBlockingResources = Array.from(scripts).some(script => {
      return !script.hasAttribute('async') && !script.hasAttribute('defer');
    }) || stylesheets.length > 3;

    const totalResources = images.length + scripts.length + stylesheets.length;

    // More accurate timing estimates based on actual content
    const baseLoadTime = headResponse ? loadTime : 2500;
    const resourcePenalty = totalResources * 15;
    const sizePenalty = htmlSize > 100000 ? (htmlSize / 100000) * 200 : 0;
    
    const estimatedLoadTime = baseLoadTime + resourcePenalty + sizePenalty;
    
    // Calculate CLS based on images without dimensions
    const imagesWithoutDimensions = Array.from(images).filter(img => 
      !img.hasAttribute('width') || !img.hasAttribute('height')
    ).length;
    const estimatedCLS = Math.min(0.5, imagesWithoutDimensions * 0.05);

    return {
      loadTime: estimatedLoadTime,
      firstContentfulPaint: estimatedLoadTime * 0.35,
      largestContentfulPaint: estimatedLoadTime * 0.65,
      cumulativeLayoutShift: estimatedCLS,
      timeToInteractive: estimatedLoadTime * 1.1,
      totalResources,
      htmlSize,
      hasLargeImages,
      hasRenderBlockingResources,
    };
  }

  private async analyzeSEO(): Promise<SEOData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    if (!doc) {
      return {
        hasTitle: false,
        hasDescription: false,
        hasH1: false,
        hasMultipleH1: false,
        hasStructuredData: false,
        hasCanonical: false,
        titleLength: 0,
        descriptionLength: 0,
        imageAltTags: 0,
        totalImages: 0,
        hasOpenGraph: false,
        hasTwitterCards: false,
        hasRobotsTxt: false,
        hasSitemap: false,
        hasMetaRobots: false,
        headingStructure: false,
        internalLinks: 0,
        externalLinks: 0,
        hasFavicon: false,
        hasHreflang: false,
      };
    }

    const title = doc.querySelector('title')?.textContent?.trim() ?? '';
    const description = doc.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() ?? '';
    const h1Elements = doc.querySelectorAll('h1');
    const structuredData = doc.querySelectorAll('script[type="application/ld+json"]');
    const canonical = doc.querySelector('link[rel="canonical"]');
    const images = doc.querySelectorAll('img');
    const imagesWithAlt = Array.from(images).filter(
      (img) => img.hasAttribute('alt') && img.getAttribute('alt')!.trim().length > 0
    ).length;

    // Open Graph
    const hasOpenGraph = !!(
      doc.querySelector('meta[property="og:title"]') &&
      doc.querySelector('meta[property="og:description"]') &&
      doc.querySelector('meta[property="og:image"]')
    );

    // Twitter Cards
    const hasTwitterCards = !!(
      doc.querySelector('meta[name="twitter:card"]') ||
      doc.querySelector('meta[property="twitter:card"]')
    );

    // Meta robots
    const hasMetaRobots = !!doc.querySelector('meta[name="robots"]');

    // Heading structure (h1 -> h2 -> h3 in order)
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let headingStructure = true;
    let lastLevel = 0;
    headings.forEach(h => {
      const level = parseInt(h.tagName[1]);
      if (level > lastLevel + 1 && lastLevel !== 0) {
        headingStructure = false;
      }
      lastLevel = level;
    });

    // Links analysis
    const links = doc.querySelectorAll('a[href]');
    let internalLinks = 0;
    let externalLinks = 0;
    links.forEach(link => {
      const href = link.getAttribute('href') || '';
      if (href.startsWith('http') && !href.includes(this.domain)) {
        externalLinks++;
      } else if (href.startsWith('/') || href.includes(this.domain)) {
        internalLinks++;
      }
    });

    // Favicon
    const hasFavicon = !!(
      doc.querySelector('link[rel="icon"]') ||
      doc.querySelector('link[rel="shortcut icon"]') ||
      doc.querySelector('link[rel="apple-touch-icon"]')
    );

    // Hreflang
    const hasHreflang = !!doc.querySelector('link[rel="alternate"][hreflang]');

    // Check for robots.txt and sitemap references
    const lowerHtml = html.toLowerCase();
    const hasRobotsTxt = lowerHtml.includes('/robots.txt') || lowerHtml.includes('robots');
    const hasSitemap = lowerHtml.includes('sitemap.xml') || lowerHtml.includes('sitemap');

    return {
      hasTitle: title.length > 0,
      hasDescription: description.length > 0,
      hasH1: h1Elements.length > 0,
      hasMultipleH1: h1Elements.length > 1,
      hasStructuredData: structuredData.length > 0,
      hasCanonical: !!canonical,
      titleLength: title.length,
      descriptionLength: description.length,
      imageAltTags: imagesWithAlt,
      totalImages: images.length,
      hasOpenGraph,
      hasTwitterCards,
      hasRobotsTxt,
      hasSitemap,
      hasMetaRobots,
      headingStructure,
      internalLinks,
      externalLinks,
      hasFavicon,
      hasHreflang,
    };
  }

  private async analyzeSecurity(): Promise<SecurityData> {
    const hasSSL = this.url.startsWith('https://');
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    // Count external scripts
    const scripts = doc?.querySelectorAll('script[src]') || [];
    let externalScripts = 0;
    scripts.forEach(script => {
      const src = script.getAttribute('src') || '';
      if (src.startsWith('http') && !src.includes(this.domain)) {
        externalScripts++;
      }
    });

    // Check for mixed content indicators
    const mixedContent = hasSSL && (
      html.includes('http://') && 
      (html.includes('src="http://') || html.includes("src='http://"))
    );

    return {
      hasSSL,
      hasHSTS: hasSSL, // Assume HSTS if SSL is present
      mixedContent,
      hasSecurityHeaders: hasSSL,
      externalScripts,
    };
  }

  private async analyzeMobile(): Promise<MobileData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    if (!doc) {
      return {
        isResponsive: false,
        hasViewport: false,
        viewportCorrect: false,
        touchFriendly: false,
        fontSizeReadable: true,
        hasMediaQueries: false,
        tapTargetsOk: false,
      };
    }

    const viewport = doc.querySelector('meta[name="viewport"]');
    const hasViewport = !!viewport;
    const viewportContent = viewport?.getAttribute('content') ?? '';
    const isResponsive = viewportContent.includes('width=device-width');
    const viewportCorrect = isResponsive && !viewportContent.includes('user-scalable=no');

    // Check for media queries in inline styles
    const hasMediaQueries = html.includes('@media') || 
      html.includes('min-width') || 
      html.includes('max-width');

    // Check for touch-friendly elements
    const buttons = doc.querySelectorAll('button, a, input[type="submit"]');
    const tapTargetsOk = buttons.length > 0;

    // Check for small font sizes
    const fontSizeReadable = !html.includes('font-size: 10px') && 
      !html.includes('font-size: 11px') &&
      !html.includes('font-size:10px') &&
      !html.includes('font-size:11px');

    return {
      isResponsive,
      hasViewport,
      viewportCorrect,
      touchFriendly: isResponsive && tapTargetsOk,
      fontSizeReadable,
      hasMediaQueries,
      tapTargetsOk,
    };
  }

  private async analyzeAccessibility(): Promise<AccessibilityData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    if (!doc) {
      return {
        hasLangAttribute: false,
        hasSkipLinks: false,
        hasAriaLabels: false,
        colorContrast: 3.0,
        formLabels: 0,
        totalForms: 0,
        hasAltTexts: false,
        hasHeadingStructure: false,
        hasFocusStyles: false,
        tabIndex: 0,
      };
    }

    const hasLangAttribute = !!doc.documentElement.getAttribute('lang');
    const hasSkipLinks = !!(
      doc.querySelector('a[href="#main"]') ||
      doc.querySelector('a[href="#content"]') ||
      doc.querySelector('.skip-link') ||
      doc.querySelector('.skip-to-content') ||
      doc.querySelector('[class*="skip"]')
    );

    const ariaElements = doc.querySelectorAll('[aria-label], [aria-labelledby], [aria-describedby], [role]');
    const hasAriaLabels = ariaElements.length > 5;

    const forms = doc.querySelectorAll('form');
    const formInputs = doc.querySelectorAll('input, select, textarea');
    const formLabels = doc.querySelectorAll('label');
    
    // Check if labels are properly associated
    const labeledInputs = Array.from(formInputs).filter(input => {
      const id = input.getAttribute('id');
      if (id) {
        return !!doc.querySelector(`label[for="${id}"]`);
      }
      return input.closest('label') !== null;
    }).length;

    // Check for alt texts
    const images = doc.querySelectorAll('img');
    const imagesWithAlt = Array.from(images).filter(img => 
      img.hasAttribute('alt') && img.getAttribute('alt')!.trim().length > 0
    ).length;
    const hasAltTexts = images.length === 0 || imagesWithAlt / images.length > 0.8;

    // Check heading structure
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const hasHeadingStructure = headings.length > 0 && !!doc.querySelector('h1');

    // Check for focus styles
    const hasFocusStyles = html.includes(':focus') || 
      html.includes('focus:') || 
      html.includes('focus-visible');

    // Check tabindex usage
    const tabIndexElements = doc.querySelectorAll('[tabindex]');

    return {
      hasLangAttribute,
      hasSkipLinks,
      hasAriaLabels,
      colorContrast: 4.5, // Assume reasonable contrast unless we can measure
      formLabels: labeledInputs,
      totalForms: formInputs.length,
      hasAltTexts,
      hasHeadingStructure,
      hasFocusStyles,
      tabIndex: tabIndexElements.length,
    };
  }

  private async analyzeDesign(): Promise<DesignData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    if (!doc) {
      return {
        modernDesign: false,
        consistentBranding: false,
        visualHierarchy: false,
        whitespace: false,
        hasCustomFonts: false,
        hasAnimations: false,
        colorScheme: false,
        layoutQuality: 30,
      };
    }

    // Check for modern CSS features
    const hasFlexbox = html.includes('display: flex') || html.includes('display:flex') || html.includes('flex');
    const hasGrid = html.includes('display: grid') || html.includes('display:grid') || html.includes('grid-');
    const hasTailwind = html.includes('tailwind') || html.includes('tw-');
    const hasBootstrap = html.includes('bootstrap') || html.includes('btn-primary');

    // Check for custom fonts
    const hasCustomFonts = !!(
      doc.querySelector('link[href*="fonts.googleapis.com"]') ||
      doc.querySelector('link[href*="fonts.gstatic.com"]') ||
      doc.querySelector('link[href*="typekit"]') ||
      html.includes('@font-face') ||
      html.includes('font-family')
    );

    // Check for animations
    const hasAnimations = html.includes('animation') || 
      html.includes('transition') || 
      html.includes('transform') ||
      html.includes('@keyframes');

    // Check for color scheme / CSS variables
    const colorScheme = html.includes('--') || 
      html.includes(':root') ||
      html.includes('var(--');

    // Visual hierarchy check
    const hasHeadings = doc.querySelectorAll('h1, h2, h3').length >= 3;
    const hasSections = doc.querySelectorAll('section, article, main').length >= 2;
    const visualHierarchy = hasHeadings && hasSections;

    // Whitespace check (look for padding/margin usage)
    const whitespace = html.includes('padding') || 
      html.includes('margin') || 
      html.includes('gap') ||
      html.includes('space-');

    // Modern design indicators
    const modernDesign = (hasFlexbox || hasGrid) && (hasTailwind || hasBootstrap || hasCustomFonts);

    // Consistent branding (logo, consistent colors)
    const hasLogo = !!(
      doc.querySelector('img[alt*="logo"]') ||
      doc.querySelector('img[class*="logo"]') ||
      doc.querySelector('[class*="logo"]') ||
      doc.querySelector('svg[class*="logo"]')
    );
    const consistentBranding = hasLogo && hasCustomFonts;

    // Calculate layout quality score
    let layoutQuality = 0;
    if (hasFlexbox || hasGrid) layoutQuality += 25;
    if (hasTailwind || hasBootstrap) layoutQuality += 20;
    if (hasCustomFonts) layoutQuality += 15;
    if (hasAnimations) layoutQuality += 15;
    if (visualHierarchy) layoutQuality += 15;
    if (whitespace) layoutQuality += 10;

    return {
      modernDesign,
      consistentBranding,
      visualHierarchy,
      whitespace,
      hasCustomFonts,
      hasAnimations,
      colorScheme,
      layoutQuality,
    };
  }

  private async analyzeCTA(): Promise<CTAData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';

    if (!doc) {
      return {
        hasCTA: false,
        ctaVisibility: 0,
        ctaCount: 0,
        ctaPlacement: false,
        ctaContrast: false,
        hasAboveFoldCTA: false,
        ctaTexts: [],
      };
    }

    // More comprehensive CTA detection
    const ctaSelectors = [
      'button',
      'a.btn', 'a.button', 'a.cta',
      '[role="button"]',
      '.btn', '.button', '.cta',
      'a[class*="btn"]', 'a[class*="button"]', 'a[class*="cta"]',
      'input[type="submit"]', 'input[type="button"]',
      '[class*="call-to-action"]',
      '[class*="primary-button"]',
      '[class*="action-button"]',
    ];

    const ctaElements = doc.querySelectorAll(ctaSelectors.join(', '));
    const ctaCount = ctaElements.length;
    const hasCTA = ctaCount > 0;

    // Extract CTA texts for analysis
    const ctaTexts: string[] = [];
    ctaElements.forEach(el => {
      const text = el.textContent?.trim();
      if (text && text.length < 50) {
        ctaTexts.push(text);
      }
    });

    // Check for above-fold CTA (in header or first section)
    const header = doc.querySelector('header, nav, [class*="hero"], [class*="banner"]');
    const hasAboveFoldCTA = header ? header.querySelectorAll(ctaSelectors.join(', ')).length > 0 : false;

    // Check CTA contrast (look for contrasting colors)
    const ctaContrast = html.includes('bg-') || 
      html.includes('background-color') ||
      html.includes('btn-primary') ||
      html.includes('btn-success');

    // Calculate visibility score
    let ctaVisibility = 0;
    if (hasCTA) ctaVisibility += 30;
    if (hasAboveFoldCTA) ctaVisibility += 30;
    if (ctaContrast) ctaVisibility += 20;
    if (ctaCount >= 3) ctaVisibility += 20;

    return {
      hasCTA,
      ctaVisibility,
      ctaCount,
      ctaPlacement: hasAboveFoldCTA && ctaCount >= 3,
      ctaContrast,
      hasAboveFoldCTA,
      ctaTexts,
    };
  }

  private async analyzeTrustSignals(): Promise<TrustData> {
    const doc = this.cachedDoc;
    const html = this.cachedHtml || '';
    const lowerHtml = html.toLowerCase();

    if (!doc) {
      return {
        hasContactInfo: false,
        hasPrivacyPolicy: false,
        hasTestimonials: false,
        hasSocialProof: false,
        hasSocialLinks: false,
        hasPhoneNumber: false,
        hasEmail: false,
        hasAddress: false,
        hasTerms: false,
        hasCertifications: false,
      };
    }

    // Contact info detection
    const hasContactInfo = lowerHtml.includes('contact') ||
      lowerHtml.includes('kontakt') ||
      lowerHtml.includes('get in touch') ||
      lowerHtml.includes('ta kontakt');

    // Phone number detection (various formats)
    const phoneRegex = /(\+47|0047)?[\s-]?\d{2,3}[\s-]?\d{2,3}[\s-]?\d{2,3}|tel:|phone|telefon/i;
    const hasPhoneNumber = phoneRegex.test(html);

    // Email detection
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|mailto:/i;
    const hasEmail = emailRegex.test(html);

    // Address detection
    const hasAddress = lowerHtml.includes('address') ||
      lowerHtml.includes('adresse') ||
      lowerHtml.includes('location') ||
      lowerHtml.includes('beliggenhet') ||
      /\d{4}\s+[a-zA-ZæøåÆØÅ]+/.test(html); // Norwegian postal code pattern

    // Privacy policy
    const hasPrivacyPolicy = lowerHtml.includes('privacy') ||
      lowerHtml.includes('personvern') ||
      lowerHtml.includes('gdpr') ||
      lowerHtml.includes('cookies') ||
      lowerHtml.includes('informasjonskapsler');

    // Terms of service
    const hasTerms = lowerHtml.includes('terms') ||
      lowerHtml.includes('vilkår') ||
      lowerHtml.includes('betingelser') ||
      lowerHtml.includes('conditions');

    // Testimonials
    const hasTestimonials = lowerHtml.includes('testimonial') ||
      lowerHtml.includes('review') ||
      lowerHtml.includes('anmeldelse') ||
      lowerHtml.includes('omtale') ||
      lowerHtml.includes('kundehistorie') ||
      lowerHtml.includes('case study') ||
      lowerHtml.includes('success story');

    // Social proof
    const hasSocialProof = lowerHtml.includes('customer') ||
      lowerHtml.includes('client') ||
      lowerHtml.includes('kunde') ||
      lowerHtml.includes('partner') ||
      lowerHtml.includes('trusted by') ||
      lowerHtml.includes('used by') ||
      /\d+\s*(kunder|customers|clients|users|brukere)/i.test(html);

    // Social media links
    const hasSocialLinks = !!(
      doc.querySelector('a[href*="facebook.com"]') ||
      doc.querySelector('a[href*="twitter.com"]') ||
      doc.querySelector('a[href*="linkedin.com"]') ||
      doc.querySelector('a[href*="instagram.com"]') ||
      doc.querySelector('a[href*="youtube.com"]') ||
      doc.querySelector('[class*="social"]')
    );

    // Certifications/badges
    const hasCertifications = lowerHtml.includes('certified') ||
      lowerHtml.includes('sertifisert') ||
      lowerHtml.includes('iso') ||
      lowerHtml.includes('award') ||
      lowerHtml.includes('badge') ||
      lowerHtml.includes('accredited');

    return {
      hasContactInfo,
      hasPrivacyPolicy,
      hasTestimonials,
      hasSocialProof,
      hasSocialLinks,
      hasPhoneNumber,
      hasEmail,
      hasAddress,
      hasTerms,
      hasCertifications,
    };
  }

  // Scoring methods
  private calculatePageSpeedScore(data: PageSpeedData): number {
    let score = 100;

    // Load time penalties
    if (data.loadTime > 4000) score -= 45;
    else if (data.loadTime > 3000) score -= 35;
    else if (data.loadTime > 2000) score -= 25;
    else if (data.loadTime > 1500) score -= 15;
    else if (data.loadTime > 1000) score -= 8;

    // LCP penalties
    if (data.largestContentfulPaint > 4000) score -= 25;
    else if (data.largestContentfulPaint > 2500) score -= 18;
    else if (data.largestContentfulPaint > 1800) score -= 10;

    // CLS penalties
    if (data.cumulativeLayoutShift > 0.25) score -= 20;
    else if (data.cumulativeLayoutShift > 0.1) score -= 12;
    else if (data.cumulativeLayoutShift > 0.05) score -= 5;

    // Resource penalties
    if (data.hasLargeImages) score -= 8;
    if (data.hasRenderBlockingResources) score -= 10;
    if (data.totalResources > 50) score -= 8;
    else if (data.totalResources > 30) score -= 4;

    // HTML size penalty
    if (data.htmlSize > 200000) score -= 10;
    else if (data.htmlSize > 100000) score -= 5;

    return Math.max(0, Math.min(100, score));
  }

  private calculateMobileScore(data: MobileData): number {
    let score = 0;
    if (data.hasViewport) score += 15;
    if (data.viewportCorrect) score += 10;
    if (data.isResponsive) score += 25;
    if (data.hasMediaQueries) score += 15;
    if (data.touchFriendly) score += 15;
    if (data.fontSizeReadable) score += 10;
    if (data.tapTargetsOk) score += 10;
    return Math.min(100, score);
  }

  private calculateSEOScore(data: SEOData): number {
    let score = 0;

    // Title
    if (data.hasTitle) score += 6;
    if (data.titleLength >= 30 && data.titleLength <= 60) score += 6;
    else if (data.titleLength > 0 && data.titleLength < 70) score += 3;

    // Description
    if (data.hasDescription) score += 6;
    if (data.descriptionLength >= 120 && data.descriptionLength <= 160) score += 6;
    else if (data.descriptionLength > 50) score += 3;

    // Headings
    if (data.hasH1) score += 6;
    if (!data.hasMultipleH1) score += 4;
    if (data.headingStructure) score += 5;

    // Technical SEO
    if (data.hasStructuredData) score += 10;
    if (data.hasCanonical) score += 6;
    if (data.hasOpenGraph) score += 6;
    if (data.hasTwitterCards) score += 4;
    if (data.hasMetaRobots) score += 3;
    if (data.hasRobotsTxt) score += 4;
    if (data.hasSitemap) score += 6;
    if (data.hasFavicon) score += 3;
    if (data.hasHreflang) score += 4;

    // Images
    const altRatio = data.totalImages > 0 ? data.imageAltTags / data.totalImages : 1;
    if (altRatio >= 0.9) score += 10;
    else if (altRatio >= 0.7) score += 6;
    else if (altRatio >= 0.5) score += 3;

    // Links
    if (data.internalLinks >= 5) score += 5;

    return Math.min(100, score);
  }

  private calculateSecurityScore(data: SecurityData): number {
    let score = 0;
    if (data.hasSSL) score += 50;
    if (data.hasHSTS) score += 20;
    if (!data.mixedContent) score += 15;
    if (data.hasSecurityHeaders) score += 10;
    if (data.externalScripts < 10) score += 5;
    return Math.min(100, score);
  }

  private calculateAccessibilityScore(data: AccessibilityData): number {
    let score = 0;
    if (data.hasLangAttribute) score += 12;
    if (data.hasSkipLinks) score += 10;
    if (data.hasAriaLabels) score += 15;
    if (data.hasAltTexts) score += 15;
    if (data.hasHeadingStructure) score += 12;
    if (data.hasFocusStyles) score += 10;

    // Color contrast
    if (data.colorContrast >= 7) score += 15;
    else if (data.colorContrast >= 4.5) score += 10;
    else if (data.colorContrast >= 3) score += 5;

    // Form labels
    const labelRatio = data.totalForms > 0 ? Math.min(1, data.formLabels / data.totalForms) : 1;
    score += Math.round(labelRatio * 11);

    return Math.min(100, score);
  }

  private calculateDesignScore(data: DesignData): number {
    let score = 0;
    if (data.modernDesign) score += 20;
    if (data.consistentBranding) score += 15;
    if (data.visualHierarchy) score += 15;
    if (data.whitespace) score += 10;
    if (data.hasCustomFonts) score += 12;
    if (data.hasAnimations) score += 8;
    if (data.colorScheme) score += 8;
    score += Math.round(data.layoutQuality * 0.12);
    return Math.min(100, score);
  }

  private calculateCTAScore(data: CTAData): number {
    let score = 0;
    if (data.hasCTA) score += 20;
    if (data.hasAboveFoldCTA) score += 25;
    if (data.ctaContrast) score += 15;
    if (data.ctaPlacement) score += 15;
    
    if (data.ctaCount >= 5) score += 15;
    else if (data.ctaCount >= 3) score += 10;
    else if (data.ctaCount >= 1) score += 5;

    score += Math.min(10, Math.round(data.ctaVisibility * 0.1));
    return Math.min(100, score);
  }

  private calculateTrustScore(data: TrustData): number {
    let score = 0;
    if (data.hasContactInfo) score += 10;
    if (data.hasPhoneNumber) score += 10;
    if (data.hasEmail) score += 8;
    if (data.hasAddress) score += 8;
    if (data.hasPrivacyPolicy) score += 12;
    if (data.hasTerms) score += 8;
    if (data.hasTestimonials) score += 15;
    if (data.hasSocialProof) score += 12;
    if (data.hasSocialLinks) score += 10;
    if (data.hasCertifications) score += 7;
    return Math.min(100, score);
  }

  private calculateTotalScore(metrics: AnalysisResult['metrics']): number {
    const weights = {
      pageSpeed: 0.18,
      mobileResponsive: 0.16,
      seo: 0.20,
      security: 0.08,
      accessibility: 0.08,
      design: 0.12,
      cta: 0.10,
      trustSignals: 0.08,
    };

    const totalScore =
      metrics.pageSpeed * weights.pageSpeed +
      metrics.mobileResponsive * weights.mobileResponsive +
      metrics.seo * weights.seo +
      metrics.security * weights.security +
      metrics.accessibility * weights.accessibility +
      metrics.design * weights.design +
      metrics.cta * weights.cta +
      metrics.trustSignals * weights.trustSignals;

    return Math.round(totalScore);
  }

  private generateIssues(
    pageSpeedData: PageSpeedData,
    seoData: SEOData,
    securityData: SecurityData,
    mobileData: MobileData,
    accessibilityData: AccessibilityData,
    designData: DesignData,
    ctaData: CTAData,
    trustData: TrustData,
    metrics: AnalysisResult['metrics'],
    isPreferredDomain: boolean = false
  ): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];

    const speedThreshold = isPreferredDomain ? 70 : 80;
    const mobileThreshold = isPreferredDomain ? 70 : 80;
    const seoThreshold = isPreferredDomain ? 65 : 75;
    const securityThreshold = isPreferredDomain ? 70 : 80;
    const designThreshold = isPreferredDomain ? 60 : 70;
    const ctaThreshold = isPreferredDomain ? 65 : 75;
    const trustThreshold = isPreferredDomain ? 55 : 65;
    const accessibilityThreshold = isPreferredDomain ? 60 : 70;

    // PAGE SPEED
    if (metrics.pageSpeed < speedThreshold) {
      const loadSec = (pageSpeedData.loadTime / 1000).toFixed(1);
      const lcpSec = (pageSpeedData.largestContentfulPaint / 1000).toFixed(1);
      const severity = isPreferredDomain 
        ? (metrics.pageSpeed < 40 ? 'warning' : 'info')
        : (metrics.pageSpeed < 50 ? 'critical' : 'warning');
      
      const speedIssues: string[] = [];
      const speedIssuesEn: string[] = [];
      if (pageSpeedData.hasLargeImages) {
        speedIssues.push('bilder uten optimalisering');
        speedIssuesEn.push('unoptimized images');
      }
      if (pageSpeedData.hasRenderBlockingResources) {
        speedIssues.push('render-blokkerende ressurser');
        speedIssuesEn.push('render-blocking resources');
      }
      if (pageSpeedData.totalResources > 30) {
        speedIssues.push('for mange ressurser');
        speedIssuesEn.push('too many resources');
      }

      issues.push({
        id: 'slow-loading',
        category: 'speed',
        severity,
        titleNo: isPreferredDomain ? 'Mulighet for raskere lasting' : 'Treg lastetid',
        titleEn: isPreferredDomain ? 'Opportunity for Faster Loading' : 'Slow Loading Speed',
        descriptionNo: `Nettsiden laster på ca. ${loadSec}s med LCP på ${lcpSec}s. ${speedIssues.length > 0 ? 'Problemer: ' + speedIssues.join(', ') + '.' : ''} Google anbefaler under 2,5s for LCP.`,
        descriptionEn: `Website loads in ~${loadSec}s with LCP of ${lcpSec}s. ${speedIssuesEn.length > 0 ? 'Issues: ' + speedIssuesEn.join(', ') + '.' : ''} Google recommends under 2.5s for LCP.`,
        impactNo: isPreferredDomain
          ? 'Raskere lastetider kan forbedre brukeropplevelsen og øke konverteringer.'
          : '53% av mobilbrukere forlater nettsider som tar mer enn 3 sekunder å laste. Hver sekund forsinkelse reduserer konverteringer med opptil 7%.',
        impactEn: isPreferredDomain
          ? 'Faster loading times can improve user experience and increase conversions.'
          : '53% of mobile users abandon websites that take more than 3 seconds to load. Each second of delay reduces conversions by up to 7%.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.pageSpeed,
      });
    }

    // CLS
    if (pageSpeedData.cumulativeLayoutShift > (isPreferredDomain ? 0.15 : 0.1)) {
      const severity = isPreferredDomain
        ? (pageSpeedData.cumulativeLayoutShift > 0.3 ? 'warning' : 'info')
        : (pageSpeedData.cumulativeLayoutShift > 0.25 ? 'critical' : 'warning');
      
      issues.push({
        id: 'layout-shift',
        category: 'speed',
        severity,
        titleNo: isPreferredDomain ? 'Mulighet for stabilere oppsett' : 'Ustabilt sideoppsett (Layout Shift)',
        titleEn: isPreferredDomain ? 'Opportunity for More Stable Layout' : 'Unstable Page Layout (Layout Shift)',
        descriptionNo: `CLS-verdi: ${pageSpeedData.cumulativeLayoutShift.toFixed(2)}. Google krever under 0,1 for Core Web Vitals. Bilder uten dimensjoner og dynamisk innhold forårsaker dette.`,
        descriptionEn: `CLS value: ${pageSpeedData.cumulativeLayoutShift.toFixed(2)}. Google requires under 0.1 for Core Web Vitals. Images without dimensions and dynamic content cause this.`,
        impactNo: isPreferredDomain
          ? 'Redusert layout shift gir en jevnere brukeropplevelse.'
          : 'Ustabilt oppsett fører til feilklikk og høyere avvisningsrate. Google straffer sider med høy CLS.',
        impactEn: isPreferredDomain
          ? 'Reduced layout shift provides a smoother user experience.'
          : 'Unstable layout leads to misclicks and higher bounce rates. Google penalizes pages with high CLS.',
        recommendationNo: '',
        recommendationEn: '',
        score: Math.round(Math.max(0, 100 - pageSpeedData.cumulativeLayoutShift * 300)),
      });
    }

    // MOBILE
    if (metrics.mobileResponsive < mobileThreshold) {
      const missingItems: string[] = [];
      const missingItemsEn: string[] = [];
      if (!mobileData.hasViewport) {
        missingItems.push('viewport meta-tag');
        missingItemsEn.push('viewport meta tag');
      }
      if (!mobileData.viewportCorrect) {
        missingItems.push('korrekt viewport-konfigurasjon');
        missingItemsEn.push('correct viewport configuration');
      }
      if (!mobileData.isResponsive) {
        missingItems.push('responsivt design');
        missingItemsEn.push('responsive design');
      }
      if (!mobileData.hasMediaQueries) {
        missingItems.push('media queries');
        missingItemsEn.push('media queries');
      }
      if (!mobileData.touchFriendly) {
        missingItems.push('berøringsvennlige elementer');
        missingItemsEn.push('touch-friendly elements');
      }

      const severity = isPreferredDomain
        ? (metrics.mobileResponsive < 40 ? 'warning' : 'info')
        : (metrics.mobileResponsive < 50 ? 'critical' : 'warning');

      issues.push({
        id: 'mobile-unfriendly',
        category: 'mobile',
        severity,
        titleNo: isPreferredDomain ? 'Mulighet for bedre mobilopplevelse' : 'Ikke optimalisert for mobil',
        titleEn: isPreferredDomain ? 'Opportunity for Better Mobile Experience' : 'Not Mobile Optimized',
        descriptionNo: `Mangler: ${missingItems.join(', ')}. Over 65% av nettrafikken kommer fra mobil.`,
        descriptionEn: `Missing: ${missingItemsEn.join(', ')}. Over 65% of web traffic comes from mobile.`,
        impactNo: isPreferredDomain
          ? 'Forbedret mobilopplevelse kan øke engasjement fra mobilbrukere.'
          : 'Google bruker mobil-først-indeksering. 61% av brukere forlater sider som ikke fungerer på mobil.',
        impactEn: isPreferredDomain
          ? 'Improved mobile experience can increase engagement from mobile users.'
          : 'Google uses mobile-first indexing. 61% of users leave sites that don\'t work on mobile.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.mobileResponsive,
      });
    }

    // SEO
    if (metrics.seo < seoThreshold) {
      const seoProblems: string[] = [];
      const seoProblemsEn: string[] = [];
      if (!seoData.hasTitle || seoData.titleLength < 30 || seoData.titleLength > 60) {
        seoProblems.push('tittel ikke optimal (30-60 tegn)');
        seoProblemsEn.push('title not optimal (30-60 chars)');
      }
      if (!seoData.hasDescription || seoData.descriptionLength < 120) {
        seoProblems.push('meta-beskrivelse mangler/kort');
        seoProblemsEn.push('meta description missing/short');
      }
      if (!seoData.hasStructuredData) {
        seoProblems.push('ingen strukturert data (Schema.org)');
        seoProblemsEn.push('no structured data (Schema.org)');
      }
      if (!seoData.hasCanonical) {
        seoProblems.push('manglende canonical-tag');
        seoProblemsEn.push('missing canonical tag');
      }
      if (!seoData.hasOpenGraph) {
        seoProblems.push('manglende Open Graph');
        seoProblemsEn.push('missing Open Graph');
      }
      if (seoData.hasMultipleH1) {
        seoProblems.push('flere H1-tagger');
        seoProblemsEn.push('multiple H1 tags');
      }
      if (!seoData.headingStructure) {
        seoProblems.push('feil overskriftshierarki');
        seoProblemsEn.push('incorrect heading hierarchy');
      }
      if (seoData.totalImages > 0 && seoData.imageAltTags / seoData.totalImages < 0.8) {
        seoProblems.push('bilder uten alt-tekst');
        seoProblemsEn.push('images without alt text');
      }
      if (!seoData.hasSitemap) {
        seoProblems.push('manglende sitemap');
        seoProblemsEn.push('missing sitemap');
      }
      if (!seoData.hasFavicon) {
        seoProblems.push('manglende favicon');
        seoProblemsEn.push('missing favicon');
      }

      const severity = isPreferredDomain
        ? (metrics.seo < 35 ? 'warning' : 'info')
        : (metrics.seo < 45 ? 'critical' : 'warning');

      issues.push({
        id: 'seo-issues',
        category: 'seo',
        severity,
        titleNo: isPreferredDomain ? 'SEO-optimaliseringsmuligheter' : 'SEO-mangler reduserer synligheten',
        titleEn: isPreferredDomain ? 'SEO Optimization Opportunities' : 'SEO Issues Reducing Visibility',
        descriptionNo: `Problemer: ${seoProblems.slice(0, 5).join(', ')}${seoProblems.length > 5 ? ' (+' + (seoProblems.length - 5) + ' til)' : ''}.`,
        descriptionEn: `Issues: ${seoProblemsEn.slice(0, 5).join(', ')}${seoProblemsEn.length > 5 ? ' (+' + (seoProblemsEn.length - 5) + ' more)' : ''}.`,
        impactNo: isPreferredDomain
          ? 'Forbedret SEO kan øke organisk synlighet.'
          : '75% av brukere scroller aldri forbi første side på Google. Dårlig SEO betyr tapte kunder.',
        impactEn: isPreferredDomain
          ? 'Improved SEO can increase organic visibility.'
          : '75% of users never scroll past the first page of Google. Poor SEO means lost customers.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.seo,
      });
    }

    // SECURITY
    if (!securityData.hasSSL || metrics.security < securityThreshold) {
      const missingSSL = !securityData.hasSSL;
      const severity = isPreferredDomain
        ? (missingSSL ? 'warning' : 'info')
        : (missingSSL ? 'critical' : 'warning');
      
      const securityIssues: string[] = [];
      const securityIssuesEn: string[] = [];
      if (!securityData.hasSSL) {
        securityIssues.push('manglende SSL/HTTPS');
        securityIssuesEn.push('missing SSL/HTTPS');
      }
      if (securityData.mixedContent) {
        securityIssues.push('blandet innhold (HTTP på HTTPS-side)');
        securityIssuesEn.push('mixed content (HTTP on HTTPS page)');
      }
      if (securityData.externalScripts > 10) {
        securityIssues.push('mange eksterne scripts');
        securityIssuesEn.push('many external scripts');
      }

      issues.push({
        id: 'security-issues',
        category: 'security',
        severity,
        titleNo: isPreferredDomain
          ? (missingSSL ? 'SSL-sertifikat anbefales' : 'Sikkerhetsforbedringer')
          : (missingSSL ? 'Manglende SSL-sertifikat' : 'Sikkerhetsproblemer'),
        titleEn: isPreferredDomain
          ? (missingSSL ? 'SSL Certificate Recommended' : 'Security Improvements')
          : (missingSSL ? 'Missing SSL Certificate' : 'Security Issues'),
        descriptionNo: securityIssues.length > 0 ? `Problemer: ${securityIssues.join(', ')}.` : 'Avanserte sikkerhetshoder kan legges til.',
        descriptionEn: securityIssuesEn.length > 0 ? `Issues: ${securityIssuesEn.join(', ')}.` : 'Advanced security headers can be added.',
        impactNo: isPreferredDomain
          ? 'Forbedret sikkerhet øker brukerens tillit.'
          : '85% av brukere forlater nettsider uten HTTPS. Nettlesere viser advarsler.',
        impactEn: isPreferredDomain
          ? 'Enhanced security increases user trust.'
          : '85% of users abandon websites without HTTPS. Browsers show warnings.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.security,
      });
    }

    // DESIGN
    if (metrics.design < designThreshold) {
      const designIssues: string[] = [];
      const designIssuesEn: string[] = [];
      if (!designData.modernDesign) {
        designIssues.push('mangler moderne CSS (flexbox/grid)');
        designIssuesEn.push('missing modern CSS (flexbox/grid)');
      }
      if (!designData.hasCustomFonts) {
        designIssues.push('ingen egendefinerte fonter');
        designIssuesEn.push('no custom fonts');
      }
      if (!designData.consistentBranding) {
        designIssues.push('inkonsekvent merkevarebygging');
        designIssuesEn.push('inconsistent branding');
      }
      if (!designData.visualHierarchy) {
        designIssues.push('uklar visuell hierarki');
        designIssuesEn.push('unclear visual hierarchy');
      }
      if (!designData.hasAnimations) {
        designIssues.push('ingen animasjoner/overganger');
        designIssuesEn.push('no animations/transitions');
      }

      const severity = isPreferredDomain
        ? (metrics.design < 30 ? 'warning' : 'info')
        : (metrics.design < 40 ? 'critical' : 'warning');

      issues.push({
        id: 'outdated-design',
        category: 'design',
        severity,
        titleNo: isPreferredDomain ? 'Designforbedringer' : 'Designet reduserer troverdigheten',
        titleEn: isPreferredDomain ? 'Design Improvements' : 'Design Reducing Credibility',
        descriptionNo: `Problemer: ${designIssues.join(', ')}.`,
        descriptionEn: `Issues: ${designIssuesEn.join(', ')}.`,
        impactNo: isPreferredDomain
          ? 'Forbedret design kan styrke merkevareopplevelsen.'
          : '94% av førsteinntrykk er designrelatert. 75% bedømmer troverdighet basert på design.',
        impactEn: isPreferredDomain
          ? 'Improved design can strengthen brand experience.'
          : '94% of first impressions are design-related. 75% judge credibility based on design.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.design,
      });
    }

    // CTA
    if (metrics.cta < ctaThreshold) {
      const ctaIssues: string[] = [];
      const ctaIssuesEn: string[] = [];
      if (!ctaData.hasCTA) {
        ctaIssues.push('ingen synlige CTA-er');
        ctaIssuesEn.push('no visible CTAs');
      }
      if (!ctaData.hasAboveFoldCTA) {
        ctaIssues.push('ingen CTA over folden');
        ctaIssuesEn.push('no CTA above the fold');
      }
      if (!ctaData.ctaContrast) {
        ctaIssues.push('lav CTA-kontrast');
        ctaIssuesEn.push('low CTA contrast');
      }
      if (ctaData.ctaCount < 3) {
        ctaIssues.push('for få CTA-er');
        ctaIssuesEn.push('too few CTAs');
      }

      const severity = isPreferredDomain
        ? (metrics.cta < 35 ? 'warning' : 'info')
        : (metrics.cta < 45 ? 'critical' : 'warning');

      issues.push({
        id: 'weak-cta',
        category: 'cta',
        severity,
        titleNo: isPreferredDomain ? 'Tydeligere handlingsoppfordringer' : 'Svake handlingsoppfordringer (CTA)',
        titleEn: isPreferredDomain ? 'Clearer Calls-to-Action' : 'Weak Calls-to-Action (CTAs)',
        descriptionNo: `${ctaData.ctaCount} CTA-er funnet. Problemer: ${ctaIssues.join(', ')}.`,
        descriptionEn: `${ctaData.ctaCount} CTAs found. Issues: ${ctaIssuesEn.join(', ')}.`,
        impactNo: isPreferredDomain
          ? 'Optimaliserte CTA-er kan forbedre konverteringsraten.'
          : 'Én tydelig CTA kan øke konverteringer med opptil 371%.',
        impactEn: isPreferredDomain
          ? 'Optimized CTAs can improve conversion rates.'
          : 'One clear CTA can increase conversions by up to 371%.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.cta,
      });
    }

    // TRUST SIGNALS
    if (metrics.trustSignals < trustThreshold) {
      const missingTrust: string[] = [];
      const missingTrustEn: string[] = [];
      if (!trustData.hasPhoneNumber) {
        missingTrust.push('telefonnummer');
        missingTrustEn.push('phone number');
      }
      if (!trustData.hasEmail) {
        missingTrust.push('e-postadresse');
        missingTrustEn.push('email address');
      }
      if (!trustData.hasAddress) {
        missingTrust.push('fysisk adresse');
        missingTrustEn.push('physical address');
      }
      if (!trustData.hasTestimonials) {
        missingTrust.push('kundeomtaler');
        missingTrustEn.push('customer testimonials');
      }
      if (!trustData.hasSocialProof) {
        missingTrust.push('sosialt bevis');
        missingTrustEn.push('social proof');
      }
      if (!trustData.hasPrivacyPolicy) {
        missingTrust.push('personvernerklæring');
        missingTrustEn.push('privacy policy');
      }
      if (!trustData.hasSocialLinks) {
        missingTrust.push('sosiale medier-lenker');
        missingTrustEn.push('social media links');
      }

      const severity = isPreferredDomain
        ? 'info'
        : (metrics.trustSignals < 30 ? 'warning' : 'info');

      issues.push({
        id: 'missing-trust',
        category: 'trust',
        severity,
        titleNo: isPreferredDomain ? 'Flere tillitssignaler' : 'Manglende tillitssignaler',
        titleEn: isPreferredDomain ? 'More Trust Signals' : 'Missing Trust Signals',
        descriptionNo: `Mangler: ${missingTrust.slice(0, 4).join(', ')}${missingTrust.length > 4 ? ' (+' + (missingTrust.length - 4) + ' til)' : ''}.`,
        descriptionEn: `Missing: ${missingTrustEn.slice(0, 4).join(', ')}${missingTrustEn.length > 4 ? ' (+' + (missingTrustEn.length - 4) + ' more)' : ''}.`,
        impactNo: isPreferredDomain
          ? 'Flere tillitssignaler kan øke konverteringsvilje.'
          : '72% stoler mer på bedrifter med kundeomtaler. Manglende tillit kan redusere konverteringer med 50%.',
        impactEn: isPreferredDomain
          ? 'More trust signals can increase willingness to convert.'
          : '72% trust businesses more with testimonials. Missing trust can reduce conversions by 50%.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.trustSignals,
      });
    }

    // ACCESSIBILITY
    if (metrics.accessibility < accessibilityThreshold) {
      const a11yIssues: string[] = [];
      const a11yIssuesEn: string[] = [];
      if (!accessibilityData.hasLangAttribute) {
        a11yIssues.push('manglende språk-attributt');
        a11yIssuesEn.push('missing language attribute');
      }
      if (!accessibilityData.hasSkipLinks) {
        a11yIssues.push('ingen hopp-til-innhold-lenke');
        a11yIssuesEn.push('no skip-to-content link');
      }
      if (!accessibilityData.hasAriaLabels) {
        a11yIssues.push('få ARIA-attributter');
        a11yIssuesEn.push('few ARIA attributes');
      }
      if (!accessibilityData.hasAltTexts) {
        a11yIssues.push('bilder uten alt-tekst');
        a11yIssuesEn.push('images without alt text');
      }
      if (!accessibilityData.hasHeadingStructure) {
        a11yIssues.push('manglende overskriftsstruktur');
        a11yIssuesEn.push('missing heading structure');
      }
      if (!accessibilityData.hasFocusStyles) {
        a11yIssues.push('manglende fokus-stiler');
        a11yIssuesEn.push('missing focus styles');
      }

      const severity = isPreferredDomain
        ? 'info'
        : (metrics.accessibility < 40 ? 'warning' : 'info');

      issues.push({
        id: 'accessibility-issues',
        category: 'accessibility',
        severity,
        titleNo: isPreferredDomain ? 'Tilgjengelighetsforbedringer' : 'Tilgjengelighetsproblemer',
        titleEn: isPreferredDomain ? 'Accessibility Improvements' : 'Accessibility Issues',
        descriptionNo: `Problemer: ${a11yIssues.join(', ')}.`,
        descriptionEn: `Issues: ${a11yIssuesEn.join(', ')}.`,
        impactNo: isPreferredDomain
          ? 'Forbedret tilgjengelighet når flere brukere og forbedrer SEO.'
          : 'Dårlig tilgjengelighet ekskluderer 15-20% av befolkningen og påvirker SEO negativt.',
        impactEn: isPreferredDomain
          ? 'Improved accessibility reaches more users and improves SEO.'
          : 'Poor accessibility excludes 15-20% of the population and negatively affects SEO.',
        recommendationNo: '',
        recommendationEn: '',
        score: metrics.accessibility,
      });
    }

    return issues.sort((a, b) => {
      const order = { critical: 0, warning: 1, info: 2 };
      return order[a.severity] - order[b.severity];
    });
  }
}
